/**
 * Resolves a selectedId to its UmlConnector — either the connector's own id,
 * a synthetic anchor key ("connectorId:variant", see UmlConnector.selectAnchor),
 * or a waypoint's own id (UmlConnectorPoint, connector.points[]).
 * Pure function, no state — shared by anything that needs to go from a
 * selection id (connector/anchor/waypoint) back to its owning connector.
 * @param {string|null} selectedId
 * @param {Array} connectors
 * @returns {object|null} the matching connector, or null if none match
 */
export function resolveConnector(selectedId, connectors) {
    if (!selectedId) {
        return null;
    }
    const direct = connectors.find((c) => c.id === selectedId);
    if (direct) {
        return direct;
    }
    const anchorConnectorId = selectedId.includes(':') ? selectedId.split(':')[0] : null;
    if (anchorConnectorId) {
        const anchorConnector = connectors.find((c) => c.id === anchorConnectorId);
        if (anchorConnector) {
            return anchorConnector;
        }
    }

    const r = connectors.find((c) => c.points.some((point) => point.id === selectedId)) || null;
    return r;
}

/**
 * Returns the shape ids to use for GEOMETRY (anchor math, straighten, etc.)
 * for a given real connector — accounting for interface-reduction rewiring
 * (see resolveInterfaceReduction.js). When hideInterfaces is active, a
 * connector touching a hidden interface is rewired in `visibleConnectors`
 * (same id, fromShapeId/toShapeId pointed at the implementer instead). This
 * looks up that rewired counterpart by id and returns ITS shape ids, so
 * geometry code never resolves against a hidden interface shape. Falls back
 * to the real connector's own shape ids when there's no rewired counterpart
 * (reduction inactive, or this connector isn't affected by it).
 * @param {object} realConnector - connector from the engine (umlConnectorEngine.connectors)
 * @param {Array} visibleConnectors - resolveInterfaceReduction(...).visibleConnectors
 * @returns {{fromShapeId: string, toShapeId: string}}
 */
export function resolveGeometryShapeIds(realConnector, visibleConnectors) {
    const visible = visibleConnectors.find((c) => c.id === realConnector.id);
    return {
        fromShapeId: visible ? visible.fromShapeId : realConnector.fromShapeId,
        toShapeId: visible ? visible.toShapeId : realConnector.toShapeId,
    };
}
